/**
 * GTFS 静的 stops (GtfsStatic API 由来) の型定義と番線列挙ロジック。
 *
 * - stops データは ControlPanel API (Cognito 認証) の `GET /gtfs-stops` プロキシ経由で
 *   取得する。取得処理は `BroadcastPointsClient.getGtfsStops` を参照。
 * - レスポンスは GtfsStatic API のパススルー形で、`stops[]` ではなく **`data[]`** を持つ
 *   (`data/routeStops.ts` の `RouteStopListFile` とはキー名が異なる点に注意)。
 * - 用途は放送条件「停車番線」(`BroadcastCondition.arrival_stop_id`) のドロップダウン候補。
 *   `route_id` では絞り込めないため、呼び出し側で全件取得・キャッシュし、
 *   {@link findPlatformStopIds} で `parent_station` フィルタして番線候補を得る。
 */

/**
 * 1 停車場分の情報 (GTFS `stops.txt` の 1 行)。
 *
 * 親 (駅) と子 (番線) の 2 階層があり、`parent_station` の有無で判別する。
 * - 親 (駅): `location_type` が `'1'` で `parent_station` は空文字。`stop_id` は駅名 (例 `'南浦和'`)。
 * - 子 (番線): `parent_station` に親の `stop_id` が入り、`stop_id` は `'<駅名>:<番線>'`
 *   (例 `'南浦和:上中'`)。**停車番線条件の値はこの子 `stop_id`**。
 *
 * `location_type` は数値ではなく **文字列** で返る点に注意 (GtfsStatic が CSV の値を
 * そのまま載せるため)。
 */
export interface GtfsStop {
  stop_id: string;
  stop_name?: string;
  location_type?: string;
  parent_station?: string;
}

/** `GET /gtfs-stops` のレスポンス (GtfsStatic API のパススルー形)。 */
export interface GtfsStopListFile {
  file?: string;
  /** 実際に採用された GTFS の運行日 (`YYYYMMDD`)。 */
  date?: string;
  /** `data` の件数 (親 + 子の合計)。 */
  count?: number;
  data: GtfsStop[];
}

/**
 * 指定した駅 (親 stop_id) に属する番線 (子 stop_id) を列挙する。
 *
 * 発車標 (OBA-TrainDepartureBoardWebApp) の
 * `frontend/src/components/departureBoard.logic.ts` の `findPlatformStopIds` と
 * **同一仕様** の再実装。挙動を変える際は両者を同期させること。
 *
 * - `parent_station === stationStopId` の子 stop_id のみを、入力順のまま返す。
 * - 該当する子が 1 件も無い場合 (= 番線分割の無い駅) は `[stationStopId]` を返す。
 *   この駅を停車番線条件に設定すると、判定側 (UpdateContents) が解決する値も
 *   親駅 ID になるため `eq` は成立する (`ne` では常に false になる)。
 * - 配列でない入力や `stop_id` を持たない要素は除外する
 *   (`data/routeStops.ts` の `sortStopsBySequence` と同じ堅牢性)。
 */
export function findPlatformStopIds(
  stops: GtfsStop[] | undefined | null,
  stationStopId: string,
): string[] {
  if (!Array.isArray(stops)) return [stationStopId];
  const platformStopIds = stops
    .filter((s): s is GtfsStop => Boolean(s) && typeof s.stop_id === 'string')
    .filter((s) => s.parent_station === stationStopId)
    .map((s) => s.stop_id);
  return platformStopIds.length > 0 ? platformStopIds : [stationStopId];
}

/**
 * 子 stop_id から番線名 (表示用ラベル) を取り出す。
 *
 * 発車標 (OBA-TrainDepartureBoardWebApp) の
 * `frontend/src/components/departureBoard.logic.ts` の `platformLabel` と
 * **同一仕様** の再実装。挙動を変える際は両者を同期させること。
 *
 * - `stopId` が `` `${stationStopId}:` `` で始まる場合はその接頭辞を除去して返す
 *   (例: `'南浦和:上中'` + `'南浦和'` → `'上中'`)。
 * - 接頭辞が一致しない場合 (番線分割の無い駅で自駅 ID がそのまま入る場合を含む) は
 *   `stopId` をそのまま返す。
 */
export function platformLabel(stopId: string, stationStopId: string): string {
  const prefix = `${stationStopId}:`;
  return stopId.startsWith(prefix) ? stopId.slice(prefix.length) : stopId;
}
